<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center h-full">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <!-- No Data State -->
    <div v-else-if="!hasData" class="flex items-center justify-center h-full text-gray-500">
      {{ $t('reports.no_data') || 'No data available' }}
    </div>

    <!-- Tabs + Single Table -->
    <div
      v-else
      class="flex flex-col flex-1 overflow-hidden"
      style="zoom: 0.9"
    >
      <!-- Question Tabs -->
      <div class="flex gap-1 px-2 pt-2 border-b overflow-x-auto shrink-0">
        <button
          v-for="question in surveyQuestions"
          :key="question.id"
          class="px-4 py-2 text-xs font-medium rounded-t-lg whitespace-nowrap transition-colors"
          :class="selectedQuestionId === question.id
            ? 'bg-primary-900 text-white'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
          @click="selectedQuestionId = question.id"
        >
          {{ question.title }}
        </button>
      </div>

      <!-- Selected Question Table -->
      <div
        v-if="selectedQuestion"
        class="flex flex-col flex-1 overflow-y-auto p-4"
      >
        <!-- Per Agent Variant -->
        <template v-if="reportVariant === 'per-agent'">
          <table class="w-full table-auto text-sm">
            <thead class="sticky top-0 z-10 bg-primary-900 text-white">
              <tr>
                <th class="px-2 py-2 text-center font-weight-medium rounded-s-lg">
                  {{ $t('reports.csat_report.agent_name') || 'Agent' }}
                </th>
                <template v-for="(col, colIdx) in questionColumns" :key="col.field">
                  <th class="px-2 py-2 text-center font-weight-medium">{{ $t(`reports.csat_report.${col.label}`) }}</th>
                  <th class="px-2 py-2 text-center font-weight-medium" :class="{ 'rounded-e-lg': colIdx === questionColumns.length - 1 }">%</th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in getDataForQuestion(selectedQuestion)"
                :key="row.agent || idx"
                :class="idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
              >
                <td class="px-2 py-2 text-center border-b border-gray-200">
                  {{ getAgentFullName(row.agent) }}
                </td>
                <template v-for="col in questionColumns" :key="col.field">
                  <td class="px-2 py-2 text-center border-b border-gray-200">{{ getRatingValue(row, selectedQuestion.prefix, col.field) }}</td>
                  <td class="px-2 py-2 text-center border-b border-gray-200">{{ getRatingValue(row, selectedQuestion.prefix, col.rateField) }}%</td>
                </template>
              </tr>
            </tbody>
          </table>
        </template>

        <!-- Per Month Variant -->
        <template v-else-if="reportVariant === 'per-month'">
          <table class="w-full table-auto text-sm">
            <thead class="sticky top-0 z-10 bg-primary-900 text-white">
              <tr>
                <th class="px-2 py-2 text-center font-weight-medium rounded-s-lg"></th>
                <th class="px-2 py-2 text-center font-weight-medium">{{ $t('reports.csat_report.month') || 'Month' }}</th>
                <template v-for="(col, colIdx) in questionColumns" :key="col.field">
                  <th class="px-2 py-2 text-center font-weight-medium">{{ $t(`reports.csat_report.${col.label}`) }}</th>
                  <th class="px-2 py-2 text-center font-weight-medium" :class="{ 'rounded-e-lg': colIdx === questionColumns.length - 1 }">%</th>
                </template>
              </tr>
            </thead>
            <tbody>
              <template
                v-for="(monthData, monthIdx) in getDataForQuestion(selectedQuestion)"
                :key="monthData.period || monthData.month"
              >
                <tr
                  :class="monthIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                  class="cursor-pointer hover:bg-gray-100"
                  @click="toggleMonth(selectedQuestion.id, monthData.period || monthData.month)"
                >
                  <td class="px-2 py-2 text-center border-b border-gray-200">
                    <v-icon size="small" color="primary-900">
                      {{ expandedMonths[`${selectedQuestion.id}_${monthData.period || monthData.month}`] ? 'mdi-minus-circle' : 'mdi-plus-circle-outline' }}
                    </v-icon>
                  </td>
                  <td class="px-2 py-2 text-center border-b border-gray-200 font-weight-bold">
                    {{ monthData.period || monthData.month }}
                  </td>
                  <template v-for="col in questionColumns" :key="col.field">
                    <td class="px-2 py-2 text-center border-b border-gray-200">{{ getRatingValue(monthData, selectedQuestion.prefix, col.field) }}</td>
                    <td class="px-2 py-2 text-center border-b border-gray-200">{{ getRatingValue(monthData, selectedQuestion.prefix, col.rateField) }}%</td>
                  </template>
                </tr>
                <!-- Day rows -->
                <tr
                  v-for="day in (monthData.days || [])"
                  v-show="expandedMonths[`${selectedQuestion.id}_${monthData.period || monthData.month}`]"
                  :key="day.period || day.day"
                  :class="(monthIdx % 2 === 0 ? 'bg-gray-50' : 'bg-white') + ' pl-8'"
                >
                  <td class="px-2 py-2 text-center border-b border-gray-200"></td>
                  <td class="px-2 py-2 text-center border-b border-gray-200">{{ formatDayOnly(day.period || day.day) }}</td>
                  <template v-for="col in questionColumns" :key="col.field">
                    <td class="px-2 py-2 text-center border-b border-gray-200">{{ getRatingValue(day, selectedQuestion.prefix, col.field) }}</td>
                    <td class="px-2 py-2 text-center border-b border-gray-200">{{ getRatingValue(day, selectedQuestion.prefix, col.rateField) }}%</td>
                  </template>
                </tr>
              </template>
            </tbody>
          </table>
        </template>

        <!-- Per Call Variant -->
        <template v-else-if="reportVariant === 'per-call'">
          <table class="w-full table-auto text-sm">
            <thead class="sticky top-0 z-10 bg-primary-900 text-white">
              <tr>
                <th class="px-2 py-2 text-center font-weight-medium rounded-s-lg"></th>
                <th class="px-2 py-2 text-center font-weight-medium"></th>
                <th class="px-2 py-2 text-center font-weight-medium">{{ $t('reports.csat_report.month') || 'Month' }}</th>
                <th class="px-2 py-2 text-center font-weight-medium">{{ $t('reports.csat_report.agent_name') || 'Agent' }}</th>
                <template v-for="(col, colIdx) in questionColumns" :key="col.field">
                  <th class="px-2 py-2 text-center font-weight-medium">{{ $t(`reports.csat_report.${col.label}`) }}</th>
                  <th class="px-2 py-2 text-center font-weight-medium" :class="{ 'rounded-e-lg': colIdx === questionColumns.length - 1 }">%</th>
                </template>
              </tr>
            </thead>
            <tbody>
              <template
                v-for="(monthData, monthIdx) in getDataForQuestion(selectedQuestion)"
                :key="monthData.period || monthData.month"
              >
                <!-- Month summary row -->
                <tr
                  :class="monthIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                  class="cursor-pointer hover:bg-gray-100"
                  @click="toggleMonth(selectedQuestion.id, monthData.period || monthData.month)"
                >
                  <td class="px-2 py-2 text-center border-b border-gray-200">
                    <v-icon size="small" color="primary-900">
                      {{ expandedMonths[`${selectedQuestion.id}_${monthData.period || monthData.month}`] ? 'mdi-minus-circle' : 'mdi-plus-circle-outline' }}
                    </v-icon>
                  </td>
                  <td class="px-2 py-2 text-center border-b border-gray-200"></td>
                  <td class="px-2 py-2 text-center border-b border-gray-200 font-weight-bold">
                    {{ monthData.period || monthData.month }}
                  </td>
                  <td class="px-2 py-2 text-center border-b border-gray-200">-</td>
                  <template v-for="col in questionColumns" :key="col.field">
                    <td class="px-2 py-2 text-center border-b border-gray-200">{{ getRatingValue(monthData, selectedQuestion.prefix, col.field) }}</td>
                    <td class="px-2 py-2 text-center border-b border-gray-200">{{ getRatingValue(monthData, selectedQuestion.prefix, col.rateField) }}%</td>
                  </template>
                </tr>
                <!-- Day rows -->
                <template v-for="(dayData, dayIdx) in (monthData.days || [])" :key="dayData.period || dayIdx">
                  <tr
                    v-show="expandedMonths[`${selectedQuestion.id}_${monthData.period || monthData.month}`]"
                    :class="(monthIdx % 2 === 0 ? 'bg-gray-50' : 'bg-white') + ' cursor-pointer hover:bg-gray-100 pl-8'"
                    @click.stop="toggleDay(selectedQuestion.id, monthData.period || monthData.month, dayData.period)"
                  >
                    <td class="px-2 py-2 text-center border-b border-gray-200"></td>
                    <td class="px-2 py-2 text-center border-b border-gray-200">
                      <v-icon size="small" color="primary-900">
                        {{ expandedDays[`${selectedQuestion.id}_${monthData.period || monthData.month}_${dayData.period}`] ? 'mdi-minus-circle' : 'mdi-plus-circle-outline' }}
                      </v-icon>
                    </td>
                    <td class="px-2 py-2 text-center border-b border-gray-200 font-weight-medium">
                      {{ formatDayOnly(dayData.period) }}
                    </td>
                    <td class="px-2 py-2 text-center border-b border-gray-200">-</td>
                    <template v-for="col in questionColumns" :key="col.field">
                      <td class="px-2 py-2 text-center border-b border-gray-200">{{ getRatingValue(dayData, selectedQuestion.prefix, col.field) }}</td>
                      <td class="px-2 py-2 text-center border-b border-gray-200">{{ getRatingValue(dayData, selectedQuestion.prefix, col.rateField) }}%</td>
                    </template>
                  </tr>
                  <!-- Call rows -->
                  <tr
                    v-for="(call, callIdx) in (dayData.calls || [])"
                    v-show="expandedMonths[`${selectedQuestion.id}_${monthData.period || monthData.month}`] && expandedDays[`${selectedQuestion.id}_${monthData.period || monthData.month}_${dayData.period}`]"
                    :key="`${call.number}_${call.agent}_${callIdx}`"
                    :class="(monthIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50') + ' pl-16'"
                  >
                    <td class="px-2 py-2 text-center border-b border-gray-200"></td>
                    <td class="px-2 py-2 text-center border-b border-gray-200"></td>
                    <td class="px-2 py-2 text-center border-b border-gray-200">{{ call.number || '-' }}</td>
                    <td class="px-2 py-2 text-center border-b border-gray-200">{{ getAgentFullName(call.agent) }}</td>
                    <template v-for="col in questionColumns" :key="col.field">
                      <td class="px-2 py-2 text-center border-b border-gray-200">{{ getRatingValue(call, selectedQuestion.prefix, col.field) }}</td>
                      <td class="px-2 py-2 text-center border-b border-gray-200">{{ getRatingValue(call, selectedQuestion.prefix, col.rateField) }}%</td>
                    </template>
                  </tr>
                </template>
              </template>
            </tbody>
          </table>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "CSATTableReport",
  props: {
    reportData: {
      type: [Array, Object],
      default: () => ({}),
    },
    selectedFilters: {
      type: Object,
      default: () => ({}),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    agents: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      expandedMonths: {},
      expandedDays: {},
      selectedQuestionId: null,
    };
  },
  computed: {
    ...mapGetters(["csatQuestions"]),
    surveyQuestions() {
      if (!this.csatQuestions || this.csatQuestions.length === 0) {
        return [];
      }
      return [...this.csatQuestions]
        .map((q) => ({
          id: q.id,
          title: q.name,
          type: this.getQuestionType(q.question_type),
          apiKey: q.message,
          prefix: this.detectPrefix(q),
        }));
    },
    selectedQuestion() {
      if (!this.surveyQuestions.length) return null;
      return this.surveyQuestions.find((q) => q.id === this.selectedQuestionId)
        || this.surveyQuestions[0];
    },
    // Columns rendered for the selected question, keyed by its scale. Each
    // entry is a count column plus its rate column.
    questionColumns() {
      const columns = {
        yesno: [
          { label: "yes", field: "yes", rateField: "yes_rate" },
          { label: "no", field: "no", rateField: "no_rate" },
        ],
        nps: [
          { label: "promoters", field: "promote", rateField: "promote_rate" },
          { label: "passives", field: "passive", rateField: "passive_rate" },
          { label: "detractors", field: "detract", rateField: "detract_sat_rate" },
        ],
        rating3: [
          { label: "satisfied", field: "sat", rateField: "sat_rate" },
          { label: "neutral", field: "natural", rateField: "natural_rate" },
          { label: "dissatisfied", field: "dissat", rateField: "dissat_rate" },
        ],
        rating: [
          { label: "very_satisfied", field: "very_sat", rateField: "very_sat_rate" },
          { label: "satisfied", field: "sat", rateField: "sat_rate" },
          { label: "neutral", field: "natural", rateField: "natural_rate" },
          { label: "dissatisfied", field: "dissat", rateField: "dissat_rate" },
          { label: "very_dissatisfied", field: "very_dissat", rateField: "very_dissat_rate" },
        ],
      };
      return columns[this.selectedQuestion?.type] || columns.rating;
    },
    reportVariant() {
      return this.selectedFilters?.reportVariant || 'per-agent';
    },
    hasData() {
      if (typeof this.reportData === 'object' && this.reportData !== null) {
        return Object.keys(this.reportData).length > 0;
      }
      return false;
    },
  },
  watch: {
    surveyQuestions(newVal) {
      if (newVal.length && !this.selectedQuestionId) {
        this.selectedQuestionId = newVal[0].id;
      }
    },
  },
  methods: {
    ...mapActions(["GetCSATQuestions"]),
    getQuestionType(questionType) {
      switch (questionType) {
        case 1: return "rating";
        case 2: return "yesno";
        case 3: return "nps";
        case 4: return "rating3";
        default: return "rating";
      }
    },
    detectPrefix(question) {
      const type = this.getQuestionType(question.question_type);
      const data = this.reportData?.[question.message];
      if (!data || data.length === 0) return "";
      const firstRow = data[0];
      const keys = Object.keys(firstRow);
      const suffixMap = {
        rating: "_very_sat",
        rating3: "_natural",
        yesno: "_yes",
        nps: "_promote",
      };
      const suffix = suffixMap[type];
      if (suffix) {
        const key = keys.find((k) => k.endsWith(suffix));
        if (key) return key.replace(suffix, "");
      }
      return "";
    },
    toggleMonth(questionId, month) {
      const key = `${questionId}_${month}`;
      this.expandedMonths = {
        ...this.expandedMonths,
        [key]: !this.expandedMonths[key],
      };
    },
    toggleDay(questionId, month, day) {
      const key = `${questionId}_${month}_${day}`;
      this.expandedDays = {
        ...this.expandedDays,
        [key]: !this.expandedDays[key],
      };
    },
    getDataForQuestion(question) {
      if (!this.reportData || typeof this.reportData !== 'object') {
        return [];
      }

      if (this.reportData[question.apiKey]) {
        return this.reportData[question.apiKey];
      }

      return [];
    },
    getRatingValue(row, prefix, field) {
      const key = `${prefix}_${field}`;
      return row[key] ?? 0;
    },
    formatDayOnly(dateStr) {
      if (!dateStr) return '-';
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        return parseInt(parts[2], 10).toString();
      }
      return dateStr;
    },
    getAgentFullName(username) {
      if (!username) return '-';
      const agent = this.agents.find((a) => a.value === username);
      return agent ? agent.label : username;
    },
  },
  async created() {
    if (!this.csatQuestions || this.csatQuestions.length === 0) {
      await this.GetCSATQuestions();
    }
    if (this.surveyQuestions.length) {
      this.selectedQuestionId = this.surveyQuestions[0].id;
    }
  },
};
</script>

<style scoped>
.set-border {
  border: 0.5px solid #d7eaf3;
}
</style>
